import { Badge, Box, Group, Stack, Table, Tabs, Text } from "@mantine/core"
import { CheckIcon, EyeIcon, PlayIcon, PlusIcon } from "@phosphor-icons/react"
import dayjs from "dayjs"
import { useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate, useSearchParams } from "react-router-dom"
import useSWR from "swr"
import { matchesBusinessName } from "@dehub/theme"
import * as CustodyTransferApi from "../../apis/custody-transfer"
import { ICustodyTransfer } from "../../apis/custody-transfer"
import { message } from "../../utils/message"
import { DeliveryActionButton, DeliveryRowAction } from "../delivery/action-controls"
import { CodeStack, TableFrame, TablePager } from "../delivery/atoms"
import { useDeliveryActions } from "../delivery/delivery-actions"
import { translateBackendError } from "../delivery/translate-error"
import { ListState } from "../ui"
import { CustodyStatusBadge } from "./atoms"
import { CreateHandoverDialog } from "./CreateHandoverDialog"
import classes from "./custody.module.css"
import { HandoverFiltersToolbar, HandoverQuickFilter } from "./HandoverFiltersToolbar"
import { HandoverStatsCards } from "./HandoverStatsCards"
import { LoadConfirmQueue, useLoadConfirmQueue } from "./LoadConfirmQueue"
import { useCustodyMutations } from "./hooks"
import { ReceiverVerifyDialog } from "./ReceiverVerifyDialog"
import { SenderVerifyDialog } from "./SenderVerifyDialog"

type StageFilter = "PENDING" | "DONE" | "CANCELLED"

const STAGE_STATUSES: Record<StageFilter, string[]> = {
  PENDING: ["DRAFT", "INITIATED", "SENDER_VERIFIED", "AWAITING_RECEIVER"],
  DONE: ["RECEIVER_VERIFIED", "COMPLETED"],
  CANCELLED: ["CANCELLED"],
}

const STAGE_KEYS: Record<StageFilter, string> = {
  PENDING: "custody.summaryStatus.PENDING",
  DONE: "custody.summaryStatus.DONE",
  CANCELLED: "custody.summaryStatus.CANCELLED",
}

function classifyVariant(ct: ICustodyTransfer) {
  if (ct.status === "CANCELLED") return "FAILED"
  if (ct.discrepancies && ct.discrepancies.length > 0) return "WARNING"
  if (ct.status === "RECEIVER_VERIFIED" || ct.status === "COMPLETED") return "READY"
  if (ct.transferType === "CARRIER_TO_CUSTOMER" && ct.deliveryNote?.refCode?.includes("PICKUP")) return "PICKUP"
  return "IN_PROGRESS"
}

function avgHandoverMinutes(rows: ICustodyTransfer[]): number | null {
  const durations: number[] = []
  for (const r of rows) {
    if (r.senderVerifiedAt && r.receiverVerifiedAt) {
      durations.push(dayjs(r.receiverVerifiedAt).diff(dayjs(r.senderVerifiedAt), "minute"))
    }
  }
  if (durations.length === 0) return null
  return Math.round(durations.reduce((s, n) => s + n, 0) / durations.length)
}

function SubTabCount({ count, active }: Readonly<{ count: number; active: boolean }>) {
  return (
    <Badge size="md" radius="xl" ff="monospace" color={active ? "dark" : "gray"} variant={active ? "filled" : "light"}>
      {count}
    </Badge>
  )
}

/** Хүлээлцэл tab (canvas Ord_DeliveryHub, hand): KPI, filters + «Хүлээлцэл үүсгэх», table. */
export function CustodyTransfers() {
  const { t } = useTranslation("delivery")
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const stage = (searchParams.get("stage") as StageFilter) ?? "PENDING"
  const routeId = searchParams.get("routeId") ?? undefined
  const [quick, setQuick] = useState<HandoverQuickFilter>("ALL")
  const [search, setSearch] = useState("")
  const [createOpen, setCreateOpen] = useState(false)
  const view = searchParams.get("view") === "LOADS" ? "LOADS" : "HANDOVER"
  const loads = useLoadConfirmQueue()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [senderTarget, setSenderTarget] = useState<ICustodyTransfer | null>(null)
  const [receiverTarget, setReceiverTarget] = useState<ICustodyTransfer | null>(null)
  const { stateOf, permissionState } = useDeliveryActions()
  const { initiate, submitting } = useCustodyMutations()

  const stageOptions = (Object.keys(STAGE_KEYS) as StageFilter[]).map((s) => ({ value: s, label: t(STAGE_KEYS[s]) }))

  const { data, isLoading, error, mutate } = useSWR(
    ["custody-transfers.list", stage, routeId],
    () => CustodyTransferApi.list({ status: STAGE_STATUSES[stage], routeId }),
    { revalidateOnFocus: false },
  )

  const rows = useMemo(() => data?.rows ?? [], [data?.rows])

  const derivedCounts = useMemo(() => {
    const counts = { all: rows.length, ready: 0, inProgress: 0, failed: 0, pickup: 0 }
    for (const r of rows) {
      const v = classifyVariant(r)
      if (v === "READY") counts.ready++
      else if (v === "FAILED") counts.failed++
      else if (v === "PICKUP") counts.pickup++
      else counts.inProgress++
    }
    return counts
  }, [rows])

  const filteredRows = useMemo(() => {
    let r = rows
    const needle = search.trim().toLowerCase()
    if (needle) {
      r = r.filter(
        (ct) =>
          ct.deliveryNote?.refCode?.toLowerCase().includes(needle) ||
          ct.route?.routeNumber?.toLowerCase().includes(needle) ||
          (ct.route?.name ?? "").toLowerCase().includes(needle) ||
          matchesBusinessName(ct.deliveryNote?.receiverBusiness, search),
      )
    }
    if (quick !== "ALL") {
      r = r.filter((ct) => {
        const v = classifyVariant(ct)
        return quick === v || (quick === "IN_PROGRESS" && v === "WARNING")
      })
    }
    return r
  }, [rows, search, quick])

  const handleViewChange = (next: string | null) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev)
      params.set("view", next === "LOADS" ? "LOADS" : "HANDOVER")
      return params
    })
  }

  const handleStageChange = (next: string | null) => {
    setPage(1)
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev)
      params.set("stage", (next as StageFilter) ?? "PENDING")
      return params
    })
    setQuick("ALL")
  }

  const handleInitiate = async (ct: ICustodyTransfer) => {
    try {
      await initiate(ct.id)
      message.success(t("custody.detail.initiateSuccess"))
    } catch (e) {
      message.error(translateBackendError(e))
    }
  }

  const filterApplied = quick !== "ALL" || search.trim() !== ""
  const safePage = Math.min(page, Math.max(1, Math.ceil(filteredRows.length / pageSize)))

  const renderTable = () => {
    if (isLoading) return <ListState state="loading" columns={6} />
    if (error) return <ListState state="error" onRetry={() => mutate()} />
    if (filteredRows.length === 0) {
      return filterApplied ? (
        <ListState
          state="noResults"
          action={{
            onClick: () => {
              setQuick("ALL")
              setSearch("")
            },
          }}
        />
      ) : (
        <ListState state="empty" title={t("manage.handover.emptyTitle")} description={t("custody.noSessions")} />
      )
    }
    return (
      <TableFrame
        footer={
          <TablePager page={safePage} pageSize={pageSize} total={filteredRows.length} onPageChange={setPage} onPageSizeChange={setPageSize} />
        }
      >
        <Table.ScrollContainer minWidth={980}>
          <Table highlightOnHover verticalSpacing="sm" horizontalSpacing="md">
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={110}>{t("manage.col.action")}</Table.Th>
                <Table.Th>{t("manage.col.dnOrder")}</Table.Th>
                <Table.Th>{t("manage.handover.colRoute")}</Table.Th>
                <Table.Th>{t("manage.handover.colItems")}</Table.Th>
                <Table.Th>{t("manage.handover.colType")}</Table.Th>
                <Table.Th>{t("manage.handover.colStatus")}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {filteredRows.slice((safePage - 1) * pageSize, safePage * pageSize).map((ct) => {
                const first = ct.expectedItems?.[0]
                const more = (ct.expectedItems?.length ?? 0) - 1
                const subject = { status: ct.status, fromUserId: ct.fromUserId, toUserId: ct.toUserId }
                const initiateState = stateOf("handoverInitiate", subject)
                const senderState = stateOf("senderVerify", subject)
                const receiverState = stateOf("receiverVerify", subject)
                return (
                  <Table.Tr key={ct.id} className={classes.row} onClick={() => navigate(`/handover/${ct.id}`)}>
                    <Table.Td onClick={(e) => e.stopPropagation()}>
                      <Group gap="xs" wrap="nowrap">
                        <DeliveryRowAction
                          label={t("manage.handover.detail")}
                          icon={<EyeIcon />}
                          state={stateOf("handoverDetail", subject)}
                          onClick={() => navigate(`/handover/${ct.id}`)}
                        />
                        {initiateState.applies && (
                          <DeliveryRowAction
                            label={t("custody.detail.start")}
                            icon={<PlayIcon />}
                            state={initiateState}
                            loading={submitting}
                            onClick={() => handleInitiate(ct)}
                          />
                        )}
                        {senderState.applies && (
                          <DeliveryRowAction
                            label={t("custody.detail.senderVerifyCta")}
                            icon={<CheckIcon />}
                            state={senderState}
                            onClick={() => setSenderTarget(ct)}
                          />
                        )}
                        {receiverState.applies && (
                          <DeliveryRowAction
                            label={t("custody.detail.receiverVerifyCta")}
                            icon={<CheckIcon />}
                            state={receiverState}
                            onClick={() => setReceiverTarget(ct)}
                          />
                        )}
                      </Group>
                    </Table.Td>
                    <Table.Td>
                      <CodeStack primary={ct.deliveryNote?.refCode ?? "-"} />
                    </Table.Td>
                    <Table.Td>
                      <Text ff="monospace" fz="var(--nw-fz-mono)">
                        {ct.route?.routeNumber ?? "-"}
                      </Text>
                      {(ct.route?.name || ct.deliveryLabel) && (
                        <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
                          {ct.route?.name ?? ct.deliveryLabel}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td maw={380}>
                      <Text fz="var(--nw-fz-body)" truncate>
                        {first
                          ? t("manage.handover.itemLine", {
                              name: first.name ?? first.sku ?? "-",
                              qty: first.quantity,
                              unit: first.unit ?? "",
                            })
                          : "-"}
                      </Text>
                      {more > 0 && (
                        <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
                          {t("manage.handover.more", { count: more })}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td>
                      <Text fz="var(--nw-fz-body)">
                        {t(`custody.transferTypeBadge.${ct.transferType}`, { defaultValue: "-" })}
                      </Text>
                    </Table.Td>
                    <Table.Td>
                      <CustodyStatusBadge status={ct.status} />
                    </Table.Td>
                  </Table.Tr>
                )
              })}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      </TableFrame>
    )
  }

  return (
    <Stack gap="md">
      <Box>
        <Text fw={650} fz="var(--nw-fz-drawer)">
          {t("custody.listTitle")}
        </Text>
        <Text fz="var(--nw-fz-body)" c="var(--nw-text-2)">
          {t("custody.listSubtitle")}
        </Text>
      </Box>

      {loads.enabled ? (
        <Tabs value={view} onChange={handleViewChange}>
          <Tabs.List>
            <Tabs.Tab value="HANDOVER" rightSection={<SubTabCount count={rows.length} active={view === "HANDOVER"} />}>
              {t("manage.handover.tabHandover")}
            </Tabs.Tab>
            <Tabs.Tab value="LOADS" rightSection={<SubTabCount count={loads.rows.length} active={view === "LOADS"} />}>
              {t("manage.handover.tabLoadConfirm")}
            </Tabs.Tab>
          </Tabs.List>
        </Tabs>
      ) : null}

      {view === "LOADS" && loads.enabled ? (
        <LoadConfirmQueue
          rows={loads.rows}
          isLoading={loads.isLoading}
          onConfirmed={() => Promise.all([loads.mutate(), mutate()])}
        />
      ) : (
        <>
          <HandoverStatsCards
            active={rows.length}
            ready={derivedCounts.ready}
            failed={derivedCounts.failed}
            avgMinutes={avgHandoverMinutes(rows)}
          />

          <HandoverFiltersToolbar
            stage={stage}
            onStageChange={handleStageChange}
            stageOptions={stageOptions}
            quick={quick}
            onQuickChange={(v) => {
              setQuick(v)
              setPage(1)
            }}
            counts={derivedCounts}
            search={search}
            onSearchChange={(v) => {
              setSearch(v)
              setPage(1)
            }}
            action={
              // O46/O185: manual handover creation (POST /custody/transfers, ORD_DN_MNG).
              <DeliveryActionButton
                state={permissionState("handoverCreate")}
                leftSection={<PlusIcon size={16} />}
                onClick={() => setCreateOpen(true)}
              >
                {t("manage.handover.create")}
              </DeliveryActionButton>
            }
          />

          {renderTable()}
        </>
      )}

      <CreateHandoverDialog opened={createOpen} onClose={() => setCreateOpen(false)} onCreated={() => mutate()} />
      {senderTarget && (
        <SenderVerifyDialog
          opened
          onClose={() => setSenderTarget(null)}
          transferId={senderTarget.id}
          transfer={senderTarget}
        />
      )}
      {receiverTarget && (
        <ReceiverVerifyDialog
          opened
          onClose={() => setReceiverTarget(null)}
          transferId={receiverTarget.id}
          transfer={receiverTarget}
        />
      )}
    </Stack>
  )
}
